<script setup lang="ts">
import type { CommandPaletteItem, NavigationMenuItem } from '@nuxt/ui'
import { usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'

import DashboardUserMenu from '@/components/dashboard/DashboardUserMenu.vue'
import { useDashboard } from '@/composables/useDashboard'
import { getAdminNavigationItems } from '@/lib/admin'
import { getSettingsNavigationItems } from '@/lib/settings'
import { adminCustomersPath, adminInvoiceRunsPath, adminInvoicesPath, adminProductsPath, adminRootPath } from '@/routes'
import { PermissionLevel } from '@/types/permissions'

const repositoryUrl = 'https://github.com/Zerya-Dev/licensing-platform'

useDashboard()

const page = usePage()
const open = ref(false)

function closeMobileSidebar() {
  open.value = false
}

const links = computed<NavigationMenuItem[][]>(() => [
  [
    {
      label: 'Dashboard',
      icon: 'i-lucide-house',
      to: adminRootPath(),
      active: page.url === adminRootPath(),
      onSelect: closeMobileSidebar,
    },
    {
      label: 'Products',
      icon: 'i-lucide-box',
      to: adminProductsPath(),
      active: page.url.startsWith(adminProductsPath()),
      onSelect: closeMobileSidebar,
    },
    {
      label: 'Customers',
      icon: 'i-lucide-users',
      to: adminCustomersPath(),
      active: page.url.startsWith(adminCustomersPath()),
      onSelect: closeMobileSidebar,
    },
    {
      label: 'Invoices',
      icon: 'i-lucide-receipt',
      to: adminInvoicesPath(),
      active: page.url.startsWith(adminInvoicesPath()),
      onSelect: closeMobileSidebar,
    },
    {
      label: 'Invoice runs',
      icon: 'i-lucide-play',
      to: adminInvoiceRunsPath(),
      active: page.url.startsWith(adminInvoiceRunsPath()),
      onSelect: closeMobileSidebar,
    },
    {
      label: 'Settings',
      icon: 'i-lucide-settings',
      defaultOpen: true,
      type: 'trigger',
      children: getSettingsNavigationItems(page.url, closeMobileSidebar),
    },
    ...(page.props.auth.user.permission_level === PermissionLevel.Admin
      ? [
          {
            label: 'Admin',
            icon: 'i-lucide-settings',
            defaultOpen: true,
            type: 'trigger',
            children: getAdminNavigationItems(page.url, closeMobileSidebar),
          } as const,
        ]
      : []),
  ],
  [
    {
      label: 'Source code',
      icon: 'i-lucide-github',
      to: repositoryUrl,
      target: '_blank',
    },
    {
      label: 'Report a bug',
      icon: 'i-lucide-bug',
      to: `${repositoryUrl}/issues/new/choose`,
      target: '_blank',
    },
  ],
])

// Keyboard hints shown in the command palette, mirroring the `g-*` chords
// registered in useDashboard().
const shortcutHints: Record<string, string[]> = {
  [adminRootPath()]: ['G', 'H'],
  [adminProductsPath()]: ['G', 'P'],
  [adminCustomersPath()]: ['G', 'C'],
}

const groups = computed(() => [
  {
    id: 'links',
    label: 'Go to',
    items: links.value.flat().map(
      (item): CommandPaletteItem => ({
        label: item.label,
        icon: item.icon,
        to: item.to,
        disabled: item.disabled,
        kbds: typeof item.to === 'string' ? shortcutHints[item.to] : undefined,
      }),
    ),
  },
])
</script>

<template>
  <UDashboardGroup unit="rem" storage="local">
    <UDashboardSidebar
      id="default"
      v-model:open="open"
      collapsible
      resizable
      class="bg-elevated/25"
      :ui="{ footer: 'lg:border-t lg:border-default' }"
    >
      <template #header="{ collapsed }">
        <div
          class="flex items-center gap-2"
          :class="collapsed ? 'justify-center' : 'px-1 py-2'"
        >
          <UAvatar
            icon="i-lucide-key-round"
            size="sm"
            :ui="{ root: 'bg-primary/10 text-primary ring ring-primary/25' }"
          />
          <span
            v-if="!collapsed"
            class="text-highlighted truncate font-semibold"
          >
            Zerya Licensing
          </span>
        </div>
      </template>

      <template #default="{ collapsed }">
        <UDashboardSearchButton
          :collapsed="collapsed"
          class="ring-default bg-transparent"
        />

        <UNavigationMenu
          :collapsed="collapsed"
          :items="links[0]"
          orientation="vertical"
          tooltip
          popover
        />

        <UNavigationMenu
          :collapsed="collapsed"
          :items="links[1]"
          orientation="vertical"
          tooltip
          class="mt-auto"
        />
      </template>

      <template #footer="{ collapsed }">
        <DashboardUserMenu :collapsed="collapsed" />
      </template>
    </UDashboardSidebar>

    <UDashboardSearch :groups="groups" />

    <slot />
  </UDashboardGroup>
</template>
